/**
 * Which two approved revisions are being compared.
 *
 * Each select disables the revisions on the wrong side of the other, so the
 * pair is always in sequence and the diff never has to defend against a
 * later revision being chosen as the earlier one.
 *
 * @module components/portfolio/comparison/RevisionPicker
 */

import React from "react";

import { revisionKindLabel } from "@/components/shared/governance-labels";
import { curriculumRevisionLabel } from "@/components/shared/revision-labels";
import { Divider, FormField, Heading, Icon, MutedText, Panel, SelectField } from "@/components/ui";
import type { CurriculumRevision } from "@/server/types";
import { formatDayOf } from "@/utils/calendar-dates";

export interface RevisionPickerProps {
  /** In internal sequence, earliest first. */
  revisions: readonly CurriculumRevision[];
  fromRevisionId: string;
  toRevisionId: string;
  onFromChange: (revisionId: string) => void;
  onToChange: (revisionId: string) => void;
}

/** How a revision is named in a select. */
function revisionLabel(revision: CurriculumRevision): string {
  return `${revision.institutionalLabel} (${curriculumRevisionLabel(`${revision.major}.${revision.minor}`)})`;
}

/** What one chosen revision is, under the select that chose it. */
function RevisionMetadata({ revision }: { revision: CurriculumRevision }): React.ReactElement {
  return (
    <div>
      <Heading level={3} size="md">
        {revision.institutionalLabel}
      </Heading>
      <MutedText className="mt-1">
        {curriculumRevisionLabel(`${revision.major}.${revision.minor}`)} ·{" "}
        {revisionKindLabel(revision.kind)} · approved {formatDayOf(revision.approvedAt)}
      </MutedText>
    </div>
  );
}

/**
 * @example
 * ```tsx
 * <RevisionPicker revisions={ordered} fromRevisionId={from} toRevisionId={to} … />
 * ```
 */
export function RevisionPicker({
  revisions,
  fromRevisionId,
  toRevisionId,
  onFromChange,
  onToChange,
}: RevisionPickerProps): React.ReactElement {
  const options = revisions.map((revision) => ({
    value: revision.id,
    label: revisionLabel(revision),
  }));
  const indexOf = (revisionId: string): number =>
    revisions.findIndex((revision) => revision.id === revisionId);
  const fromRevision = revisions.find((revision) => revision.id === fromRevisionId);
  const toRevision = revisions.find((revision) => revision.id === toRevisionId);

  return (
    <Panel title="Choose two curriculum revisions" density="compact">
      <div>
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Earlier revision" htmlFor="comparison-from-revision">
            <SelectField
              id="comparison-from-revision"
              value={fromRevisionId}
              onChange={onFromChange}
              options={options.map((option) => ({
                ...option,
                disabled: indexOf(option.value) >= indexOf(toRevisionId),
              }))}
              data-testid="comparison-from-revision"
            />
          </FormField>
          <FormField label="Later revision" htmlFor="comparison-to-revision">
            <SelectField
              id="comparison-to-revision"
              value={toRevisionId}
              onChange={onToChange}
              options={options.map((option) => ({
                ...option,
                disabled: indexOf(option.value) <= indexOf(fromRevisionId),
              }))}
              data-testid="comparison-to-revision"
            />
          </FormField>
        </div>
        {fromRevision && toRevision ? (
          <>
            <Divider className="mt-5" />
            <div className="grid gap-4 pt-4 md:grid-cols-2">
              <RevisionMetadata revision={fromRevision} />
              <RevisionMetadata revision={toRevision} />
            </div>
          </>
        ) : null}
      </div>
    </Panel>
  );
}

export interface ComparisonScopeBarProps {
  fromRevision: CurriculumRevision;
  toRevision: CurriculumRevision;
}

/**
 * The pair under comparison, kept on screen while the views scroll.
 *
 * Five views deep, a reader has no other reminder of which two revisions
 * every number on the page is about.
 */
export function ComparisonScopeBar({
  fromRevision,
  toRevision,
}: ComparisonScopeBarProps): React.ReactElement {
  return (
    // A `section`, not a `div`: `aria-label` is prohibited on a role-less
    // element, so the name the bar exists to carry reaches nobody.
    <section
      className="sticky top-(--sq-topbar-height) z-40 border-y border-base-300 bg-base-100/95 px-4 py-3 backdrop-blur"
      aria-label="Curriculum revisions being compared"
    >
      <div className="flex flex-wrap items-center justify-center gap-2 text-md">
        <span className="font-semibold">{fromRevision.institutionalLabel}</span>
        <MutedText as="span">
          {curriculumRevisionLabel(`${fromRevision.major}.${fromRevision.minor}`)}
        </MutedText>
        <Icon name="arrow-right" className="text-primary" aria-hidden />
        <span className="font-semibold">{toRevision.institutionalLabel}</span>
        <MutedText as="span">
          {curriculumRevisionLabel(`${toRevision.major}.${toRevision.minor}`)}
        </MutedText>
      </div>
    </section>
  );
}
